import { useEffect, useMemo, useRef, useState } from "react";
import {
  Cube,
  MagnifyingGlassPlus,
  MagnifyingGlassMinus,
  ArrowClockwise,
  ArrowsClockwise,
  CaretLeft,
  CaretRight,
  ArrowDown,
  X,
} from "@phosphor-icons/react";

const ANGLE_LABEL = { front: "Front", side: "Side", top: "Top view" };
const SPIN_RANK = { front: 0, side: 1, "": 2 };
const DRAG_PER_STEP = 90;

const normalize = (photos) =>
  (photos || [])
    .filter(Boolean)
    .map((p) => (typeof p === "string" ? { src: p, angle: "" } : { src: p.src, angle: p.angle || "" }))
    .filter((p) => p.src);

// A light "3D-style" inspector for sinks: drag to spin through the angle photos
// (front → side → …), flip to the top-down shot, scroll or use the buttons to zoom,
// then drag to pan the zoomed image.
export const SinkViewer = ({ photos, title, onClose }) => {
  const all = useMemo(() => normalize(photos), [photos]);
  const spin = useMemo(
    () =>
      all
        .filter((p) => p.angle !== "top")
        .sort((a, b) => (SPIN_RANK[a.angle] ?? 2) - (SPIN_RANK[b.angle] ?? 2)),
    [all],
  );
  const tops = useMemo(() => all.filter((p) => p.angle === "top"), [all]);
  const [mode, setMode] = useState(spin.length ? "spin" : "top");
  const list = mode === "top" ? tops : spin;
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [tilt, setTilt] = useState(0);
  const drag = useRef(null);

  useEffect(() => {
    const key = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);

  const reset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setTilt(0);
  };
  const go = (d) => {
    if (!list.length) return;
    setIdx((i) => (i + d + list.length) % list.length);
    reset();
  };
  const switchMode = (m) => {
    setMode(m);
    setIdx(0);
    reset();
  };

  const onDown = (e) => {
    const p = e.touches ? e.touches[0] : e;
    drag.current = { x: p.clientX, y: p.clientY, panx: pan.x, pany: pan.y, startIdx: idx };
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const p = e.touches ? e.touches[0] : e;
    const dx = p.clientX - drag.current.x;
    const dy = p.clientY - drag.current.y;
    if (zoom > 1) {
      setPan({ x: drag.current.panx + dx, y: drag.current.pany + dy });
    } else if (mode === "spin" && list.length > 1) {
      // turntable: every DRAG_PER_STEP px of horizontal drag advances one angle,
      // the leftover distance tilts the photo so the swap reads as a turn.
      const steps = Math.round(-dx / DRAG_PER_STEP);
      const rest = -dx - steps * DRAG_PER_STEP;
      setIdx((((drag.current.startIdx + steps) % list.length) + list.length) % list.length);
      setTilt((rest / DRAG_PER_STEP) * 22);
    }
  };
  const onUp = () => {
    drag.current = null;
    setTilt(0);
  };
  const onWheel = (e) => {
    const z = Math.min(4, Math.max(1, zoom - e.deltaY * 0.0025));
    setZoom(z);
    if (z <= 1) setPan({ x: 0, y: 0 });
  };

  const cursor = zoom > 1 ? "grab" : mode === "spin" && list.length > 1 ? "ew-resize" : "default";
  const current = list[idx];
  const label = current
    ? ANGLE_LABEL[current.angle] || (list.length > 1 ? `Angle ${idx + 1}` : "Photo")
    : "";
  const dragging = !!drag.current;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/80 p-3 backdrop-blur-sm sm:p-8"
      data-testid="sink-viewer"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${title} inspector`}
    >
      <div
        className="flex max-h-full w-full max-w-[94vw] flex-col overflow-hidden rounded-[24px] bg-white"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        <div className="flex flex-wrap items-center gap-3 bg-[#1c1b19] px-5 py-3 text-white">
          <span className="flex items-center gap-2 text-sm font-medium">
            <Cube size={16} weight="bold" /> {title} · 3D view
          </span>
          <button
            type="button"
            data-testid="sink-viewer-close"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto flex h-9 w-9 items-center justify-center rounded-lg text-white/70 transition-colors duration-150 hover:bg-white/10 hover:text-white sm:order-last sm:ml-0"
          >
            <X size={18} weight="bold" />
          </button>
          {tops.length > 0 && spin.length > 0 && (
            <div className="order-last flex items-center gap-1 rounded-xl bg-[#3a3835] p-1 text-[11px] font-semibold sm:order-none">
              <button
                type="button"
                data-testid="sink-viewer-mode-spin"
                onClick={() => switchMode("spin")}
                className={`flex h-8 items-center gap-1.5 rounded-lg px-3 transition-colors duration-150 ${
                  mode === "spin" ? "bg-white text-slate-900" : "text-white/70 hover:text-white"
                }`}
              >
                <ArrowsClockwise size={13} weight="bold" /> Spin
              </button>
              <button
                type="button"
                data-testid="sink-viewer-mode-top"
                onClick={() => switchMode("top")}
                className={`flex h-8 items-center gap-1.5 rounded-lg px-3 transition-colors duration-150 ${
                  mode === "top" ? "bg-white text-slate-900" : "text-white/70 hover:text-white"
                }`}
              >
                <ArrowDown size={13} weight="bold" /> Top view
              </button>
            </div>
          )}
          <div className="order-last ml-auto flex items-center gap-1 rounded-xl bg-[#3a3835] p-1 sm:order-none">
            <button
              type="button"
              data-testid="sink-viewer-zoom-out"
              onClick={() => onWheel({ deltaY: 200 })}
              aria-label="Zoom out"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-white/70 transition-colors duration-150 hover:bg-white/10 hover:text-white"
            >
              <MagnifyingGlassMinus size={16} weight="bold" />
            </button>
            <button
              type="button"
              data-testid="sink-viewer-zoom-in"
              onClick={() => onWheel({ deltaY: -200 })}
              aria-label="Zoom in"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-white/70 transition-colors duration-150 hover:bg-white/10 hover:text-white"
            >
              <MagnifyingGlassPlus size={16} weight="bold" />
            </button>
            <button
              type="button"
              data-testid="sink-viewer-reset"
              onClick={reset}
              aria-label="Reset view"
              className="flex h-9 w-9 items-center justify-center rounded-lg text-white/70 transition-colors duration-150 hover:bg-white/10 hover:text-white"
            >
              <ArrowClockwise size={16} weight="bold" />
            </button>
          </div>
        </div>

        <div
          className="relative h-[78vh] select-none overflow-hidden bg-[#f3f1ec]"
          style={{ cursor, touchAction: "none", perspective: "1400px" }}
          onMouseDown={onDown}
          onMouseMove={onMove}
          onMouseUp={onUp}
          onMouseLeave={onUp}
          onTouchStart={onDown}
          onTouchMove={onMove}
          onTouchEnd={onUp}
          onWheel={onWheel}
          data-testid="sink-viewer-stage"
        >
          {list.length > 0 ? (
            list.map((p, n) => (
              <img
                key={p.src}
                src={p.src}
                alt={`${title} — ${ANGLE_LABEL[p.angle] || `angle ${n + 1}`}`}
                draggable={false}
                data-testid={n === idx ? "sink-viewer-image" : undefined}
                className="pointer-events-none absolute left-1/2 top-1/2 max-h-full max-w-full object-contain"
                style={{
                  opacity: n === idx ? 1 : 0,
                  transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px)) rotateY(${
                    n === idx ? tilt : 0
                  }deg) scale(${zoom})`,
                  transition: dragging
                    ? "opacity 0.18s ease-out"
                    : "opacity 0.18s ease-out, transform 0.25s ease-out",
                }}
              />
            ))
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-slate-500">
              No photo to inspect
            </div>
          )}

          {current && (
            <div
              className="pointer-events-none absolute left-4 top-4 rounded-full bg-slate-900/85 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-white backdrop-blur"
              data-testid="sink-viewer-angle-label"
            >
              {label}
            </div>
          )}

          {list.length > 1 && (
            <>
              <button
                type="button"
                data-testid="sink-viewer-prev"
                onClick={() => go(-1)}
                aria-label="Previous angle"
                className="absolute left-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-sm backdrop-blur transition-colors duration-150 hover:bg-white"
              >
                <CaretLeft size={18} weight="bold" />
              </button>
              <button
                type="button"
                data-testid="sink-viewer-next"
                onClick={() => go(1)}
                aria-label="Next angle"
                className="absolute right-4 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-sm backdrop-blur transition-colors duration-150 hover:bg-white"
              >
                <CaretRight size={18} weight="bold" />
              </button>
              <div
                className="pointer-events-none absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 backdrop-blur"
                data-testid="sink-viewer-angle-dots"
              >
                {list.map((p, n) => (
                  <span
                    key={p.src}
                    className={`block h-1.5 rounded-full transition-all duration-200 ${
                      n === idx ? "w-5 bg-slate-900" : "w-1.5 bg-slate-400"
                    }`}
                  />
                ))}
              </div>
            </>
          )}
          <div className="pointer-events-none absolute bottom-4 right-4 rounded-xl bg-white/80 px-3 py-1.5 text-[11px] text-slate-500 backdrop-blur">
            {mode === "spin" && list.length > 1 ? "Drag to spin · " : ""}scroll to zoom
          </div>
        </div>
      </div>
    </div>
  );
};

export default SinkViewer;
